import React, { useEffect, useState } from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import Link from '@mui/joy/Link';
import Button from '@mui/joy/Button';
import { Message } from '../../types/message';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Checkbox from '@mui/joy/Checkbox';

interface MessageBubbleProps {
  message: Message;
  isOwn: boolean;
  isSelectable?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (messageReference: string) => void;
}

const STICKER_DISPLAY_PX = 168;

interface VCardContact {
  fullName: string;
  phone: string | null;
}

const GENERATED_MEDIA_BODY_PREFIXES: readonly string[] = [
  'En esta imagen se detectó:',
  '[Análisis automático del video]',
  '[Transcribiendo',
  '[Error',
];

function isGeneratedMediaBody(body: string): boolean {
  return GENERATED_MEDIA_BODY_PREFIXES.some((prefix) => body.startsWith(prefix));
}

function resolveMediaDescription(message: Message): string | null {
  const caption = message.caption?.trim();
  if (caption) {
    return caption;
  }
  const body = message.body?.trim();
  if (!body || isGeneratedMediaBody(body)) {
    return null;
  }
  return body;
}

function parseVCard(vcard: string | null | undefined): VCardContact {
  if (!vcard) {
    return { fullName: 'Contacto', phone: null };
  }
  const lines = vcard.split(/\r?\n/);
  let fullName = 'Contacto';
  let phone: string | null = null;
  for (const line of lines) {
    if (line.startsWith('FN:')) {
      fullName = line.slice(3).trim() || 'Contacto';
    }
    if (!phone && line.startsWith('TEL')) {
      const colonIndex = line.indexOf(':');
      if (colonIndex !== -1) {
        phone = line.slice(colonIndex + 1).trim() || null;
      }
    }
  }
  return { fullName, phone };
}

function getDocumentLabelFromUrl(mediaUrl: string | null | undefined): string {
  if (!mediaUrl) {
    return 'Documento';
  }
  try {
    const path = new URL(mediaUrl).pathname;
    const segment = path.split('/').filter(Boolean).pop() || 'documento';
    return decodeURIComponent(segment);
  } catch {
    const segment = mediaUrl.split('/').filter(Boolean).pop() || 'documento';
    return decodeURIComponent(segment);
  }
}

type BubbleColor = 'danger' | 'primary' | 'neutral';
type DocumentPreviewKind = 'pdf' | 'image';

function resolveDocumentPreviewKind(
  mimetype: string | null,
  fileLabel: string,
): DocumentPreviewKind | null {
  const mime = (mimetype ?? '').toLowerCase();
  const name = fileLabel.toLowerCase();
  if (mime === 'application/pdf' || name.endsWith('.pdf')) {
    return 'pdf';
  }
  if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/.test(name)) {
    return 'image';
  }
  return null;
}

function resolveDocumentTypeLabel(
  mimetype: string | null,
  previewKind: DocumentPreviewKind | null,
): string {
  if (previewKind === 'pdf') {
    return 'PDF';
  }
  if (previewKind === 'image') {
    return 'Imagen';
  }
  if (mimetype) {
    return mimetype;
  }
  return 'Documento';
}

async function fetchMediaBlob(url: string): Promise<Blob> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('fetch failed');
  }
  return response.blob();
}

function normalizePreviewBlob(
  blob: Blob,
  previewKind: DocumentPreviewKind,
  mimetype: string | null,
): Blob {
  if (previewKind === 'pdf') {
    return blob.type === 'application/pdf'
      ? blob
      : new Blob([blob], { type: 'application/pdf' });
  }
  if (blob.type.startsWith('image/')) {
    return blob;
  }
  const imageType =
    mimetype && mimetype.startsWith('image/') ? mimetype : 'image/jpeg';
  return new Blob([blob], { type: imageType });
}

function saveBlobAsFile(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

interface DocumentCardProps {
  docUrl: string;
  fileLabel: string;
  mimetype: string | null;
  isOwn: boolean;
  bubbleColor: BubbleColor;
  mediaDescription: string | null;
  footer: React.ReactNode;
}

const DocumentCard: React.FC<DocumentCardProps> = ({
  docUrl,
  fileLabel,
  mimetype,
  isOwn,
  bubbleColor,
  mediaDescription,
  footer,
}) => {
  const previewKind = resolveDocumentPreviewKind(mimetype, fileLabel);
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [actionError, setActionError] = useState<string | null>(null);
  useEffect(() => {
    return () => {
      if (previewObjectUrl) {
        URL.revokeObjectURL(previewObjectUrl);
      }
    };
  }, [previewObjectUrl]);
  const openPreview = async (): Promise<void> => {
    if (!docUrl || !previewKind || isLoadingPreview) {
      return;
    }
    setActionError(null);
    setIsLoadingPreview(true);
    try {
      const blob = await fetchMediaBlob(docUrl);
      const typedBlob = normalizePreviewBlob(blob, previewKind, mimetype);
      const objectUrl = URL.createObjectURL(typedBlob);
      setPreviewObjectUrl((current) => {
        if (current) {
          URL.revokeObjectURL(current);
        }
        return objectUrl;
      });
    } catch {
      setActionError('No se pudo previsualizar el archivo.');
    } finally {
      setIsLoadingPreview(false);
    }
  };
  const downloadFile = async (): Promise<void> => {
    if (!docUrl || isDownloading) {
      return;
    }
    setActionError(null);
    setIsDownloading(true);
    try {
      const blob = await fetchMediaBlob(docUrl);
      saveBlobAsFile(blob, fileLabel);
    } catch {
      setActionError('No se pudo descargar el archivo.');
    } finally {
      setIsDownloading(false);
    }
  };
  const closePreview = (): void => {
    setPreviewObjectUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return null;
    });
  };
  return (
    <>
      {previewObjectUrl && previewKind ? (
        <Box
          onClick={closePreview}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            bgcolor: 'rgba(0,0,0,0.88)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            cursor: 'pointer',
          }}
        >
          {previewKind === 'pdf' ? (
            <Box
              component="iframe"
              src={previewObjectUrl}
              title={fileLabel}
              onClick={(event) => event.stopPropagation()}
              sx={{
                width: 'min(96vw, 900px)',
                height: 'min(88vh, 1200px)',
                border: 0,
                borderRadius: 'md',
                bgcolor: 'background.surface',
                boxShadow: 'lg',
                cursor: 'auto',
              }}
            />
          ) : (
            <Box
              component="img"
              src={previewObjectUrl}
              alt={fileLabel}
              onClick={(event) => event.stopPropagation()}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                cursor: 'auto',
              }}
            />
          )}
        </Box>
      ) : null}
      <Sheet
        variant={isOwn ? 'solid' : 'outlined'}
        color={bubbleColor}
        sx={{
          maxWidth: 340,
          width: '100%',
          borderRadius: '18px',
          p: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          ...(isOwn
            ? { borderBottomRightRadius: 6, boxShadow: 'sm' }
            : { borderBottomLeftRadius: 6, boxShadow: 'sm' }),
        }}
      >
        {previewKind === 'pdf' ? (
          <PictureAsPdfIcon sx={{ fontSize: 28, flexShrink: 0, opacity: 0.7 }} />
        ) : (
          <InsertDriveFileIcon sx={{ fontSize: 28, flexShrink: 0, opacity: 0.7 }} />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            level="title-sm"
            noWrap
            title={fileLabel}
            sx={{ color: isOwn ? 'inherit' : 'text.primary' }}
          >
            {fileLabel}
          </Typography>
          <Typography level="body-xs" sx={{ color: isOwn ? 'inherit' : 'neutral.500' }}>
            {resolveDocumentTypeLabel(mimetype, previewKind)}
          </Typography>
          {mediaDescription ? (
            <Typography
              level="body-xs"
              noWrap
              sx={{ color: isOwn ? 'inherit' : 'text.secondary', mt: 0.25 }}
            >
              {mediaDescription}
            </Typography>
          ) : null}
          {actionError ? (
            <Typography level="body-xs" color="danger">
              {actionError}
            </Typography>
          ) : null}
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
            {previewKind && docUrl ? (
              <Button
                size="sm"
                variant="plain"
                color="neutral"
                loading={isLoadingPreview}
                onClick={() => {
                  void openPreview();
                }}
                sx={{ minHeight: 24, px: 0.5 }}
              >
                Previsualizar
              </Button>
            ) : null}
            {docUrl ? (
              <Button
                size="sm"
                variant="plain"
                color="primary"
                loading={isDownloading}
                onClick={() => {
                  void downloadFile();
                }}
                sx={{ minHeight: 24, px: 0.5 }}
              >
                Descargar
              </Button>
            ) : null}
          </Box>
          {footer}
        </Box>
      </Sheet>
    </>
  );
};

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isOwn,
  isSelectable = false,
  isSelected = false,
  onToggleSelect,
}) => {
  const [zoomMediaUrl, setZoomMediaUrl] = useState<string | null>(null);
  const [isVideoReady, setIsVideoReady] = useState<boolean>(false);

  const formatTime = (date: Date): string => {
    const d = new Date(date);
    return d.toLocaleTimeString('es-AR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return '✓';
      case 'delivered':
        return '✓✓';
      case 'read':
        return '✓✓';
      case 'failed':
        return '✗';
      default:
        return '';
    }
  };

  const isFailed = message.status === 'failed';
  const bubbleColor = isFailed ? 'danger' : isOwn ? 'primary' : 'neutral';

  const bubbleBase = {
    maxWidth: 380,
    width: 'fit-content',
    borderRadius: '18px',
    overflow: 'hidden' as const,
    ...(isOwn
      ? {
          borderBottomRightRadius: 6,
          boxShadow: 'sm',
        }
      : {
          borderBottomLeftRadius: 6,
          boxShadow: 'sm',
        }),
  };
  const timeLabel = formatTime(message.timestamp);
  const statusIcon = isOwn ? getStatusIcon(message.status) : '';
  const clock = (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
      <Typography
        level="body-xs"
        sx={{
          fontSize: '11px',
          lineHeight: 1,
          color: isOwn && !isFailed ? 'inherit' : 'neutral.500',
          opacity: 0.8,
        }}
      >
        {timeLabel}
      </Typography>
      {statusIcon ? (
        <Typography
          level="body-xs"
          sx={{
            fontSize: '11px',
            lineHeight: 1,
            color:
              message.status === 'failed'
                ? 'danger.500'
                : isOwn
                  ? 'inherit'
                  : 'neutral.500',
          }}
        >
          {statusIcon}
        </Typography>
      ) : null}
    </Box>
  );

  const renderContent = () => {
    if (message.revokedAt != null) {
      return (
        <Sheet
          variant="soft"
          color="neutral"
          sx={{
            ...bubbleBase,
            p: 1.5,
            borderStyle: 'dashed',
            borderWidth: 1,
            borderColor: 'divider',
          }}
        >
            <Typography
            level="body-sm"
            sx={{ fontStyle: 'italic', color: 'neutral.500' }}
          >
            Este mensaje fue eliminado
          </Typography>
          {clock}
        </Sheet>
      );
    }
    const mediaDescription = resolveMediaDescription(message);
    switch (message.type) {
      case 'text':
        return (
          <Sheet variant={isOwn ? 'solid' : 'outlined'} color={bubbleColor} sx={{ ...bubbleBase, p: 1.5 }}>
            <Typography level="body-sm" sx={{ color: isOwn ? 'inherit' : 'text.primary', pr: 0.5 }}>
              {message.body}
            </Typography>
            {clock}
          </Sheet>
        );

      case 'image':
        return (
          <>
            <Sheet
              variant={isOwn ? 'solid' : 'outlined'}
              color={bubbleColor}
              sx={bubbleBase}
            >
              <Box sx={{ position: 'relative' }}>
                <Box
                  component="img"
                  src={message.mediaUrl || message.imagePath || ''}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  onClick={() =>
                    setZoomMediaUrl(message.mediaUrl || message.imagePath || null)
                  }
                  sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    cursor: 'pointer',
                  }}
                />
                {mediaDescription ? null : (
                  <Typography
                    level="body-xs"
                    sx={{
                      position: 'absolute',
                      right: 8,
                      bottom: 6,
                      fontSize: '11px',
                      lineHeight: 1,
                      color: '#fff',
                      textShadow: '0 1px 2px rgba(0,0,0,0.7)',
                    }}
                  >
                    {timeLabel}
                    {statusIcon ? ` ${statusIcon}` : ''}
                  </Typography>
                )}
              </Box>
              {mediaDescription ? (
                <Box
                  sx={{ p: 1.5, cursor: 'text', userSelect: 'text' }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <Typography
                    level="body-sm"
                    sx={{
                      color: isOwn ? 'inherit' : 'text.primary',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      userSelect: 'text',
                    }}
                  >
                    {mediaDescription}
                  </Typography>
                  {clock}
                </Box>
              ) : null}
            </Sheet>
          </>
        );

      case 'audio':
      case 'ptt':
        return (
          <Sheet variant={isOwn ? 'solid' : 'outlined'} color={bubbleColor} sx={{ ...bubbleBase, p: 1.5 }}>
            <audio
              controls
              preload="none"
              src={message.mediaUrl || message.audioPath || ''}
              style={{ height: 36, maxWidth: 280 }}
            />
            {clock}
          </Sheet>
        );

      case 'video':
      case 'videoMessage':
        return (
          <Sheet
            variant={isOwn ? 'solid' : 'outlined'}
            color={bubbleColor}
            sx={{ ...bubbleBase, p: 0, maxWidth: 320 }}
          >
            {isVideoReady ? (
              <Box
                component="video"
                controls
                autoPlay
                playsInline
                src={message.mediaUrl || message.videoPath || ''}
                sx={{
                  width: '100%',
                  maxHeight: 280,
                  display: 'block',
                  verticalAlign: 'bottom',
                }}
              />
            ) : (
              <Box
                onClick={() => setIsVideoReady(true)}
                sx={{
                  height: 140,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  bgcolor: isOwn ? 'rgba(0,0,0,0.12)' : 'background.level1',
                }}
              >
                <PlayArrowIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            )}
            <Box sx={{ px: 1.5, py: 1 }}>
              {mediaDescription ? (
                <Typography
                  level="body-sm"
                  sx={{
                    color: isOwn ? 'inherit' : 'text.primary',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    mb: 0.5,
                  }}
                >
                  {mediaDescription}
                </Typography>
              ) : null}
              {clock}
            </Box>
          </Sheet>
        );

      case 'sticker': {
        const stickerSrc = message.mediaUrl || message.stickerPath || '';
        return (
          <>
          <Box
            onClick={() => stickerSrc && setZoomMediaUrl(stickerSrc)}
            sx={{
              width: STICKER_DISPLAY_PX,
              height: STICKER_DISPLAY_PX,
              maxWidth: 'min(168px, 42vw)',
              maxHeight: 'min(168px, 42vw)',
              flexShrink: 0,
              cursor: stickerSrc ? 'pointer' : 'default',
              bgcolor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src={stickerSrc}
              alt=""
              loading="lazy"
              decoding="async"
              draggable={false}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                objectPosition: 'center',
                display: 'block',
                userSelect: 'none',
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.12))',
              }}
            />
          </Box>
          {clock}
          </>
        );
      }

      case 'location':
        return (
          <Sheet variant={isOwn ? 'solid' : 'outlined'} color={bubbleColor} sx={{ ...bubbleBase, p: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <LocationOnIcon sx={{ flexShrink: 0, mt: 0.25, color: isOwn ? 'inherit' : 'text.primary' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {message.locationName ? (
                  <Typography level="title-sm" sx={{ color: isOwn ? 'inherit' : 'text.primary' }}>
                    {message.locationName}
                  </Typography>
                ) : null}
                <Typography level="body-xs" sx={{ opacity: 0.9, color: isOwn ? 'inherit' : 'text.secondary' }}>
                  {message.latitude}, {message.longitude}
                </Typography>
                <Link
                  href={`https://maps.google.com/?q=${message.latitude},${message.longitude}`}
                  target="_blank"
                  level="body-xs"
                  sx={{ mt: 0.5, color: isOwn ? 'inherit' : undefined }}
                >
                  Ver en Google Maps
                </Link>
              </Box>
            </Box>
            {clock}
          </Sheet>
        );

      case 'document':
      case 'documentMessage':
        return (
          <DocumentCard
            docUrl={message.mediaUrl || ''}
            fileLabel={getDocumentLabelFromUrl(message.mediaUrl)}
            mimetype={message.mimetype}
            isOwn={isOwn}
            bubbleColor={bubbleColor}
            mediaDescription={mediaDescription}
            footer={clock}
          />
        );

      case 'reaction':
      case 'reactionMessage': {
        const emoji = message.body || '👍';
        return (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 52,
                height: 52,
                borderRadius: '50%',
                bgcolor: isOwn ? 'primary.softBg' : 'neutral.softBg',
                boxShadow: 'sm',
                fontSize: 28,
                lineHeight: 1,
                userSelect: 'none',
              }}
            >
              {emoji}
            </Box>
            {clock}
          </>
        );
      }

      case 'contact':
      case 'contact_card':
      case 'vcard': {
        const { fullName, phone } = parseVCard(message.body);
        return (
          <Sheet
            variant={isOwn ? 'solid' : 'outlined'}
            color={bubbleColor}
            sx={{ ...bubbleBase, p: 1.5, minWidth: 220 }}
          >
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <ContactPageIcon
                sx={{
                  fontSize: 36,
                  flexShrink: 0,
                  color: isOwn ? 'inherit' : 'primary.500',
                  opacity: 0.85,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  level="title-sm"
                  sx={{
                    color: isOwn ? 'inherit' : 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fullName}
                </Typography>
                {phone ? (
                  <Typography
                    level="body-xs"
                    sx={{ color: isOwn ? 'inherit' : 'text.secondary', mt: 0.25 }}
                  >
                    {phone}
                  </Typography>
                ) : null}
                {clock}
              </Box>
            </Box>
          </Sheet>
        );
      }

      default:
        return (
          <Sheet
            variant="soft"
            color="neutral"
            sx={{
              ...bubbleBase,
              p: 1.5,
              borderStyle: 'dashed',
              borderWidth: 1,
              borderColor: 'divider',
            }}
          >
            <Typography level="body-xs" sx={{ fontStyle: 'italic', color: 'neutral.400' }}>
              Mensaje no compatible
            </Typography>
            {clock}
          </Sheet>
        );
    }
  };

  return (
    <>
      {zoomMediaUrl ? (
        <Box
          onClick={() => setZoomMediaUrl(null)}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            bgcolor: 'rgba(0,0,0,0.82)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            cursor: 'pointer',
          }}
        >
          <Box
            component="img"
            src={zoomMediaUrl}
            alt=""
            onClick={(e) => e.stopPropagation()}
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              filter: message.type === 'sticker' ? 'drop-shadow(0 4px 24px rgba(0,0,0,0.35))' : undefined,
            }}
          />
        </Box>
      ) : null}
      <Box
        sx={{
          display: 'flex',
          justifyContent: isOwn ? 'flex-end' : 'flex-start',
          alignItems: 'flex-start',
          gap: 1,
          mb: message.type === 'sticker' ? 1.25 : 2,
          px: 0.5,
        }}
      >
      {isSelectable ? (
        <Checkbox
          checked={isSelected}
          onChange={() => onToggleSelect?.(message.reference)}
          sx={{ mt: 0.5 }}
        />
      ) : null}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isOwn ? 'flex-end' : 'flex-start',
          maxWidth: 'min(100%, 420px)',
        }}
      >
        {renderContent()}
      </Box>
    </Box>
    </>
  );
};

export default React.memo(MessageBubble);
